import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslate } from 'react-redux-multilingual';
import { Modal, Button, Badge } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faReceipt } from '@fortawesome/free-solid-svg-icons';
import Moment from 'react-moment';
import axios from 'axios';
import swal from 'sweetalert2';
import { InputSearchClient, ClientForm } from '@gull';

const today = () => { const d = new Date(); return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0'); };

const handleAxiosError = (error, t) => {
    if(error === 'end_session') return;
    if(error.response === undefined){
        swal.fire(t('unknowError'), '', 'error');
    }else if(error.response.status === 403){
        swal.fire(t('forbiddenGeneral'), '', 'error');
    }else{
        swal.fire(t('unknowError'), '', 'error');
    }
};
const nowRoundedToHour = () => { const d = new Date(); d.setMinutes(0,0,0); d.setHours(d.getHours()+1); return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0')+'T'+String(d.getHours()).padStart(2,'0')+':00'; };

const formatDatetimeLocal = (d) =>
    d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0')
    +'T'+String(d.getHours()).padStart(2,'0')+':'+String(d.getMinutes()).padStart(2,'0');

export const ReservationForm = ({ table, onSubmit, onCancel, validButton, initialReservation, initialDates, defaultClientId, preloadedClient, onSubModalChange, defaultReservationTime }) => {
    const t = useTranslate();
    const [clientId, setClientId] = useState(initialReservation?.client_id || (defaultClientId && !initialReservation ? defaultClientId : false));
    const [clientError, setClientError] = useState(false);
    const [showModalAdd, setShowModalAdd] = useState(false);
    const [showModalEdit, setShowModalEdit] = useState(false);
    const [defaultClientObj, setDefaultClientObj] = useState(preloadedClient || null);
    const [loadingDefaultClient, setLoadingDefaultClient] = useState(!!defaultClientId && !initialReservation && !preloadedClient);

    // Sync depuis la prop quand le parent la reçoit après le montage
    useEffect(() => {
        if (!preloadedClient) return;
        setDefaultClientObj(preloadedClient);
        setLoadingDefaultClient(false);
    }, [preloadedClient]);

    // Fetch fallback si preloadedClient non fourni (annulé si la prop arrive entre-temps)
    useEffect(() => {
        if (!defaultClientId || initialReservation || preloadedClient) return;
        setLoadingDefaultClient(true);
        let cancelled = false;
        axios.get(process.env.REACT_APP_API_URL+'api/client/'+defaultClientId)
            .then((response) => {
                if (!cancelled) { setDefaultClientObj(response.data); setLoadingDefaultClient(false); }
            })
            .catch((error) => {
                if (!cancelled) {
                    setLoadingDefaultClient(false);
                    if(error === 'end_session') return;
                    if(error.response === undefined){
                        swal.fire(t('unknowError'), '', 'error');
                    }else if(error.response.status === 403){
                        swal.fire(t('forbiddenGeneral'), '', 'error');
                    }else{
                        swal.fire(t('unknowError'), '', 'error');
                    }
                }
            });
        return () => { cancelled = true; };
    }, [defaultClientId, preloadedClient]);

    const openSubModal = (type) => {
        if (type === 'add') setShowModalAdd(true);
        else setShowModalEdit(true);
        if (onSubModalChange) onSubModalChange(true);
    };

    const closeSubModal = (type) => {
        if (type === 'add') setShowModalAdd(false);
        else setShowModalEdit(false);
        if (onSubModalChange) onSubModalChange(false);
    };

    const existingClientName = initialReservation?.client
        ? `${initialReservation.client.name} ${initialReservation.client.firstname}`.trim()
        : null;

    const defaultDurationMs = useMemo(() => {
        if (initialDates?.debut && initialDates?.fin) {
            return new Date(initialDates.fin) - new Date(initialDates.debut);
        }
        if (initialReservation?.date_debut && initialReservation?.date_fin) {
            return new Date(initialReservation.date_fin) - new Date(initialReservation.date_debut);
        }
        if (defaultReservationTime) {
            return defaultReservationTime * 60 * 1000;
        }
        return null;
    }, [initialDates, initialReservation, defaultReservationTime]);

    const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
        defaultValues: {
            nb_personne: initialReservation?.nb_personne ?? table?.nb_personne ?? 1,
            date_debut: initialReservation?.date_debut?.slice(0, 16) ?? initialDates?.debut?.slice(0, 16) ?? nowRoundedToHour(),
            date_fin: initialReservation?.date_fin?.slice(0, 16) ?? '',
            note: initialReservation?.note ?? '',
        }
    });

    const watchedDateDebut = watch('date_debut');
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            // À l'ouverture, ne préremplir automatiquement que pour une nouvelle réservation
            // (en édition, la date de fin existante ne doit pas être écrasée)
            if (initialReservation) return;
        }
        if (!watchedDateDebut || !defaultDurationMs) return;
        const start = new Date(watchedDateDebut);
        if (isNaN(start.getTime())) return;
        setValue('date_fin', formatDatetimeLocal(new Date(start.getTime() + defaultDurationMs)));
    }, [watchedDateDebut]);

    const onValid = (data) => {
        if (!clientId) { setClientError(true); return; }
        onSubmit({ ...data, client_id: clientId });
    };

    const handleClientSelected = (id) => {
        setClientId(id);
        if (id) setClientError(false);
    };

    // Après création d'un client : sélectionne son id et met à jour l'input de recherche avec son nom
    const handleClientCreated = (clientData) => {
        setClientId(clientData.id);
        setClientError(false);
        setDefaultClientObj(clientData);
    };

    return (
        <>
            <Modal size="xl" show={showModalAdd} onHide={() => closeSubModal('add')}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('customer')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ClientForm
                        clientid={false}
                        setValue={handleClientCreated}
                        closeModal={closeSubModal}
                        moduleApp="pos"
                    />
                </Modal.Body>
            </Modal>

            <Modal size="xl" show={showModalEdit} onHide={() => closeSubModal('edit')}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('editClient')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ClientForm
                        clientid={clientId || false}
                        closeModal={closeSubModal}
                        moduleApp="pos"
                    />
                </Modal.Body>
            </Modal>

            <form onSubmit={handleSubmit(onValid)}>
                <div className="form-group mb-3">
                    <label className="form-label">{t('client')} <span className="text-danger">*</span></label>
                    {existingClientName && !clientId && (
                        <div className="mb-1">
                            <span className="badge bg-light text-dark border">{existingClientName}</span>
                            <small className="text-muted ms-2">{t('client')}</small>
                        </div>
                    )}
                    <div className={`d-flex gap-2 align-items-center${clientError ? ' is-invalid' : ''}`}>
                        <div className="flex-grow-1 position-relative">
                            <InputSearchClient
                                setClient={handleClientSelected}
                                defaultClientName={defaultClientObj ? (`${defaultClientObj.name} ${defaultClientObj.firstname || ''}`.trim() + ` - ${defaultClientObj.email} - ${defaultClientObj.tel}`) : undefined}
                                disabled={loadingDefaultClient}
                            />
                            {loadingDefaultClient && (
                                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                    style={{ background: 'rgba(255,255,255,0.7)', borderRadius: 4, zIndex: 1 }}>
                                    <span className="spinner-border spinner-border-sm text-secondary" />
                                </div>
                            )}
                        </div>
                        <button type="button" className="btn btn-success btn-sm" disabled={loadingDefaultClient} onClick={() => openSubModal('add')}>
                            <FontAwesomeIcon icon={faPlus} />
                        </button>
                        <button type="button" className="btn btn-warning btn-sm" disabled={!clientId || loadingDefaultClient} onClick={() => openSubModal('edit')}>
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                    </div>
                    {clientError && <div className="invalid-feedback d-block">{t('fieldRequired')}</div>}
                </div>
                <div className="row">
                    <div className="col-4">
                        <div className="form-group mb-3">
                            <label className="form-label">{t('reservationDateDebut')}</label>
                            <input
                                type="datetime-local"
                                className={'form-control' + (errors.date_debut ? ' is-invalid' : '')}
                                {...register('date_debut', { required: true })}
                            />
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="form-group mb-3">
                            <label className="form-label">{t('reservationDateFin')}</label>
                            <input
                                type="datetime-local"
                                className="form-control"
                                {...register('date_fin')}
                            />
                        </div>
                    </div>
                    <div className="col-4">
                        <div className="form-group mb-3">
                            <label className="form-label">{t('nbPersons')}</label>
                            <input
                                type="number"
                                className={'form-control' + (errors.nb_personne ? ' is-invalid' : '')}
                                min="1"
                                max={table?.nb_personne}
                                {...register('nb_personne', { required: true, min: 1 })}
                            />
                        </div>
                    </div>
                </div>
                <div className="form-group mb-3">
                    <label className="form-label">{t('reservationNote')}</label>
                    <textarea
                        className="form-control"
                        rows="2"
                        {...register('note')}
                    />
                </div>
                <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-sm btn-danger" onClick={onCancel}>
                        {t('btnCancel')}
                    </button>
                    <button type="submit" className="btn btn-sm btn-primary" disabled={validButton}>
                        {validButton
                            ? <span><span>&nbsp;</span><div className="spinner-border spinner-border-sm" /></span>
                            : t('btnValidate')
                        }
                    </button>
                </div>
            </form>
        </>
    );
};

const PlanTableReservationModal = ({ show, onHide, table, onReservationAdded, onReservationDeleted, onReservationUpdated, initialDates, defaultClientId = null, preloadedClient: preloadedClientProp = null, defaultReservationTime = null, onCreateTicket = null, onOpenTicket = null, loadingOpenTicket = false, referenceDatetime = null }) => {
    const t = useTranslate();
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingReservation, setEditingReservation] = useState(null);
    const [validButton, setValidButton] = useState(false);
    const [subModalOpen, setSubModalOpen] = useState(false);
    const [creatingTicketId, setCreatingTicketId] = useState(null);
    const [preloadedClient, setPreloadedClient] = useState(preloadedClientProp);

    // Utilise le client fourni par le parent ou le fetch comme fallback
    useEffect(() => {
        if (!show) { setPreloadedClient(null); return; }
        if (preloadedClientProp) { setPreloadedClient(preloadedClientProp); return; }
        if (!defaultClientId) { setPreloadedClient(null); return; }
        let cancelled = false;
        axios.get(process.env.REACT_APP_API_URL+'api/client/'+defaultClientId)
            .then((response) => { if (!cancelled) setPreloadedClient(response.data); })
            .catch((error) => { if (!cancelled) handleAxiosError(error, t); });
        return () => { cancelled = true; };
    }, [show, defaultClientId, preloadedClientProp]);

    const tableStatus = useMemo(() => {
        const now = referenceDatetime ? new Date(referenceDatetime) : new Date();
        if(reservations.some(r => {
            const start = new Date(r.date_debut);
            const end = r.date_fin ? new Date(r.date_fin) : null;
            return start <= now && (end === null || end >= now);
        })) return 'ongoing';
        if(reservations.some(r => new Date(r.date_debut) > now)) return 'upcoming';
        return 'available';
    }, [reservations, referenceDatetime]);

    useEffect(function() {
        // Toujours démonter le formulaire à la fermeture pour éviter des fetches parasites
        setShowForm(false);
        setEditingReservation(null);
        setCreatingTicketId(null);
        if(!show || !table) return;
        setLoading(true);
        // Aligne la liste sur la date recherchée dans le plan de table (sinon on reste bloqué sur aujourd'hui)
        const dateStr = referenceDatetime ? referenceDatetime.slice(0, 10) : today();
        axios.post(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation/active', {
            tbl_id: table.id,
            date_debut: dateStr+'T00:00',
            date_fin: dateStr+'T23:59',
        }).then((response) => {
            if(!response.data.valid){
                let interms='';
                var keys = Object.keys(response.data.errors);
                for(var i = 0; i < keys.length; i++){
                    interms+=response.data.errors[keys[i]].join(',')+','
                }
                interms=interms.split(',')
                let messageError='';
                interms.forEach(interm=>messageError+=t(interm)+'. ')
                messageError=messageError.replace('undefined','');
                swal.fire(messageError, '', 'error');
            }else{
                setReservations(response.data.datas)
            }
            setLoading(false)
        }).catch((error) => {
            if(error=='end_session') return;
            if(error.response==undefined){
                swal.fire(t('unknowError'), '', 'error')
            }else if(error.response.status==403){
                swal.fire(t('forbiddenGeneral'), '', 'error')
            }else{
                swal.fire(t('unknowError'), '', 'error')
            }
            setLoading(false)
        })
    }, [show, table, referenceDatetime])

    const handleAdd = (data) => {
        setValidButton(true);
        axios.post(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation', {
            tbl_id: table.id,
            client_id: data.client_id || null,
            date_debut: data.date_debut,
            date_fin: data.date_fin || null,
            nb_personne: parseInt(data.nb_personne),
            note: data.note || null,
        }).then((response) => {
            if(!response.data.valid){
                let interms='';
                var keys = Object.keys(response.data.errors);
                for(var i = 0; i < keys.length; i++){
                    interms+=response.data.errors[keys[i]].join(',')+','
                }
                interms=interms.split(',')
                let messageError='';
                interms.forEach(interm=>messageError+=t(interm)+'. ')
                messageError=messageError.replace('undefined','');
                swal.fire(messageError, '', 'error');
            }else{
                setReservations(current => [...current, response.data.data])
                if(onReservationAdded) onReservationAdded(response.data.data)
                setShowForm(false)
            }
            setValidButton(false)
        }).catch((error) => {
            if(error=='end_session') return;
            if(error.response==undefined){
                swal.fire(t('unknowError'), '', 'error')
            }else if(error.response.status==403){
                swal.fire(t('forbiddenGeneral'), '', 'error')
            }else{
                swal.fire(t('unknowError'), '', 'error')
            }
            setValidButton(false)
        })
    }

    const handleEdit = (data) => {
        setValidButton(true);
        axios.put(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation/'+editingReservation.id, {
            tbl_id: table.id,
            client_id: data.client_id !== null ? data.client_id : editingReservation.client_id,
            date_debut: data.date_debut,
            date_fin: data.date_fin || null,
            nb_personne: parseInt(data.nb_personne),
            note: data.note || null,
        }).then((response) => {
            if(!response.data.valid){
                let interms='';
                var keys = Object.keys(response.data.errors);
                for(var i = 0; i < keys.length; i++){
                    interms+=response.data.errors[keys[i]].join(',')+','
                }
                interms=interms.split(',')
                let messageError='';
                interms.forEach(interm=>messageError+=t(interm)+'. ')
                messageError=messageError.replace('undefined','');
                swal.fire(messageError, '', 'error');
            }else{
                setReservations(current => current.map(r => r.id === editingReservation.id ? response.data.data : r))
                if(onReservationUpdated) onReservationUpdated(response.data.data)
                setEditingReservation(null)
            }
            setValidButton(false)
        }).catch((error) => {
            if(error=='end_session') return;
            if(error.response==undefined){
                swal.fire(t('unknowError'), '', 'error')
            }else if(error.response.status==403){
                swal.fire(t('forbiddenGeneral'), '', 'error')
            }else{
                swal.fire(t('unknowError'), '', 'error')
            }
            setValidButton(false)
        })
    }

    const handleDelete = (reservation) => {
        swal.fire({
            title: t('confirmDeleteReservation'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('btnDelete'),
            cancelButtonText: t('btnCancel'),
        }).then((result) => {
            if(!result.isConfirmed) return;
            axios.delete(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation/'+reservation.id
            ).then(() => {
                setReservations(current => current.filter(r => r.id !== reservation.id))
                if(onReservationDeleted) onReservationDeleted(reservation.id)
            }).catch((error) => {
                if(error=='end_session') return;
                if(error.response==undefined){
                    swal.fire(t('unknowError'), '', 'error')
                }else if(error.response.status==403){
                    swal.fire(t('forbiddenGeneral'), '', 'error')
                }else{
                    swal.fire(t('unknowError'), '', 'error')
                }
            })
        })
    }

    const openEdit = (r) => {
        setShowForm(false);
        setEditingReservation(r);
    }

    const cancelEdit = () => {
        setEditingReservation(null);
    }

    const handleCreateTicketClick = async (reservationId, clientId) => {
        setCreatingTicketId(reservationId);
        try {
            if (onCreateTicket) await onCreateTicket(reservationId, clientId);
        } finally {
            setCreatingTicketId(null);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered dialogClassName={subModalOpen ? 'modal-low' : undefined}>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2 flex-wrap">
                    {t('table')} {table?.numero} — {t('reservations')}
                    {!loading && (
                        <Badge
                            bg={tableStatus === 'ongoing' ? 'secondary' : tableStatus === 'upcoming' ? 'warning' : 'success'}
                            text={tableStatus === 'upcoming' ? 'dark' : undefined}
                        >
                            {t('tableStatus' + tableStatus.charAt(0).toUpperCase() + tableStatus.slice(1))}
                        </Badge>
                    )}
                    {!loading && onOpenTicket && (() => {
                        const ongoingRes = reservations.find(r => r.ticket && r.ticket.status === 0 && (!r.ticket.commande || r.ticket.commande.cancel === 0));
                        return ongoingRes ? (
                            <button className="btn btn-sm btn-warning text-nowrap" onClick={() => onOpenTicket(ongoingRes.ticket)}>
                                <FontAwesomeIcon icon={faReceipt} className="me-1" />
                                {t('ticketEnCours')}
                            </button>
                        ) : null;
                    })()}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border" />
                    </div>
                ) : (
                    <>
                        {reservations.length === 0 && !showForm ? (
                            <p className="text-muted text-center py-2">{t('noReservationToday')}</p>
                        ) : (
                            <table className="table table-sm mb-3">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>{t('reservationDateDebut')}</th>
                                        <th>{t('reservationDateFin')}</th>
                                        <th>{t('client')}</th>
                                        <th>{t('phone')}</th>
                                        <th>{t('nbPersons')}</th>
                                        <th>{t('reservationNote')}</th>
                                        <th></th>
                                        {(onCreateTicket || onOpenTicket) && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {reservations.map(r => {
                                        const now = referenceDatetime ? new Date(referenceDatetime) : new Date();
                                        const start = new Date(r.date_debut);
                                        const end = r.date_fin ? new Date(r.date_fin) : null;
                                        const status = start > now ? 'upcoming'
                                            : (end === null || end > now) ? 'ongoing'
                                            : 'past';
                                        const rowClass = status === 'ongoing' ? 'table-secondary'
                                            : status === 'upcoming' ? 'table-warning'
                                            : '';
                                        const badgeBg = status === 'ongoing' ? 'secondary'
                                            : status === 'upcoming' ? 'warning'
                                            : 'light';
                                        const badgeLabel = status === 'ongoing' ? t('tableStatusOngoing')
                                            : status === 'past' ? t('tableStatusPast')
                                            : t('tableStatusUpcoming');
                                        return (
                                        <React.Fragment key={r.id}>
                                            <tr className={rowClass}>
                                                <td>
                                                    <Badge bg={badgeBg} text={badgeBg === 'light' || badgeBg === 'warning' ? 'dark' : undefined} className="text-nowrap" style={{ fontSize: '0.7rem' }}>
                                                        {badgeLabel}
                                                    </Badge>
                                                </td>
                                                <td><Moment format="DD/MM/YYYY HH:mm">{r.date_debut}</Moment></td>
                                                <td>{r.date_fin ? <Moment format="DD/MM/YYYY HH:mm">{r.date_fin}</Moment> : '—'}</td>
                                                <td>{r.client ? `${r.client.name} ${r.client.firstname}`.trim() : '—'}</td>
                                                <td>{r.client?.tel || '—'}</td>
                                                <td>{r.nb_personne}</td>
                                                <td>{r.note || '—'}</td>
                                                <td>
                                                    <div className="d-flex gap-1">
                                                        <button className="btn btn-sm btn-primary" onClick={() => openEdit(r)}>
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>
                                                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r)}>
                                                            <i className="i-Close-Window" />
                                                        </button>
                                                    </div>
                                                </td>
                                                {(onCreateTicket || onOpenTicket) && (
                                                    <td>
                                                        {r.ticket && r.ticket.status === 0 && (!r.ticket.commande || r.ticket.commande.cancel === 0) ? (
                                                            <button className="btn btn-sm btn-warning text-nowrap" onClick={() => onOpenTicket && onOpenTicket(r.ticket)}>
                                                                <FontAwesomeIcon icon={faReceipt} className="me-1" />
                                                                {t('ticketEnCours')}
                                                            </button>
                                                        ) : r.ticket && r.ticket.status > 0 ? (
                                                            <button className="btn btn-sm btn-primary text-nowrap" onClick={() => onOpenTicket && onOpenTicket(r.ticket)} disabled={loadingOpenTicket}>
                                                                {loadingOpenTicket ? <span className="spinner-border spinner-border-sm me-1" /> : <FontAwesomeIcon icon={faReceipt} className="me-1" />}
                                                                {t('openTicketForTable')}
                                                            </button>
                                                        ) : (
                                                            <button className="btn btn-sm btn-success text-nowrap" onClick={() => handleCreateTicketClick(r.id, r.client_id)} disabled={creatingTicketId === r.id}>
                                                                {creatingTicketId === r.id ? <span className="spinner-border spinner-border-sm me-1" /> : <FontAwesomeIcon icon={faPlus} className="me-1" />}
                                                                {t('createTicketForTable')}
                                                            </button>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                            {editingReservation?.id === r.id && (
                                                <tr>
                                                    <td colSpan={(onCreateTicket || onOpenTicket) ? 9 : 8} className="p-3 border-top">
                                                        <h6 className="mb-3">{t('editReservation')}</h6>
                                                                                <ReservationForm
                                                            key={r.id}
                                                            table={table}
                                                            onSubmit={handleEdit}
                                                            onCancel={cancelEdit}
                                                            validButton={validButton}
                                                            initialReservation={editingReservation}
                                                            initialDates={initialDates}
                                                            defaultReservationTime={defaultReservationTime}
                                                            onSubModalChange={setSubModalOpen}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}

                        {showForm ? (
                            <div className="border rounded p-3">
                                <h6 className="mb-3">{t('addReservation')}</h6>
                                <ReservationForm
                                    table={table}
                                    onSubmit={handleAdd}
                                    onCancel={() => setShowForm(false)}
                                    validButton={validButton}
                                    initialDates={initialDates}
                                    defaultClientId={defaultClientId}
                                    preloadedClient={preloadedClient}
                                    defaultReservationTime={defaultReservationTime}
                                    onSubModalChange={setSubModalOpen}
                                />
                            </div>
                        ) : (
                            !editingReservation && (
                                <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>
                                    + {t('addReservation')}
                                </Button>
                            )
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default PlanTableReservationModal;